import {
  ActionIcon,
  Badge,
  Button,
  Card,
  FileButton,
  Group,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconCirclePlus,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { useRef, useState } from 'react';
import type { CarouselCardButton } from '../../../../api/src/modules/whatsapp/schema';

/**
 * Card layout is defined ONCE for the whole carousel — Meta requires every
 * card to have an identical component structure, so consistency is enforced
 * by construction: each card only fills content into the shared layout.
 */
export interface CardLayout {
  headerFormat: 'IMAGE' | 'VIDEO';
  hasBody: boolean;
  /** Ordered button slots (max 2), identical on every card. */
  buttonTypes: Array<'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'>;
}

export interface CardDraft {
  handleId: string | null;
  /** Local object URL — preview only, never sent to the API. */
  previewUrl: string | null;
  bodyText: string;
  bodyExamples: string[];
  buttons: CarouselCardButton[];
}

export function emptyCardDraft(layout: CardLayout): CardDraft {
  return {
    handleId: null,
    previewUrl: null,
    bodyText: '',
    bodyExamples: [],
    buttons: layout.buttonTypes.map(defaultButtonForType),
  };
}

function defaultButtonForType(t: CardLayout['buttonTypes'][number]): CarouselCardButton {
  return t === 'QUICK_REPLY'
    ? { type: t, text: 'Reply' }
    : t === 'URL'
      ? { type: t, text: 'Visit', url: 'https://example.com' }
      : { type: t, text: 'Call us', phone_number: '+10000000000' };
}

const BUTTON_SLOT_OPTIONS = [
  { value: '', label: '— none —' },
  { value: 'QUICK_REPLY', label: 'Quick reply' },
  { value: 'URL', label: 'URL' },
  { value: 'PHONE_NUMBER', label: 'Phone' },
];

export function CarouselCardsEditor({
  layout,
  onLayoutChange,
  cards,
  onCardsChange,
  onUploadMedia,
}: {
  layout: CardLayout;
  onLayoutChange: (l: CardLayout) => void;
  cards: CardDraft[];
  onCardsChange: (c: CardDraft[]) => void;
  /** Returns null (and surfaces its own error toast) when the upload fails. */
  onUploadMedia: (file: File) => Promise<{ handleId: string; previewUrl: string } | null>;
}) {
  const updateCard = (i: number, patch: Partial<CardDraft>) => {
    onCardsChange(cards.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  };

  /** Upload media for card `i`; only mutates the card when a handle came back. */
  const handleCardUpload = async (i: number, file: File) => {
    const prevUrl = cards[i]?.previewUrl ?? null;
    const res = await onUploadMedia(file);
    if (!res) return; // error already surfaced by onUploadMedia
    if (prevUrl) URL.revokeObjectURL(prevUrl);
    updateCard(i, { handleId: res.handleId, previewUrl: res.previewUrl });
  };

  const removeCard = (i: number) => {
    const url = cards[i]?.previewUrl;
    if (url) URL.revokeObjectURL(url);
    onCardsChange(cards.filter((_, idx) => idx !== i));
  };

  const changeLayout = (next: CardLayout) => {
    onLayoutChange(next);
    // Propagate structural changes to every card: keep media only when the
    // header format is unchanged; rebuild button slots; clear body when off.
    onCardsChange(
      cards.map((c) => ({
        ...c,
        handleId: next.headerFormat === layout.headerFormat ? c.handleId : null,
        previewUrl: next.headerFormat === layout.headerFormat ? c.previewUrl : null,
        bodyText: next.hasBody ? c.bodyText : '',
        bodyExamples: next.hasBody ? c.bodyExamples : [],
        buttons: next.buttonTypes.map((t, slot) =>
          c.buttons[slot]?.type === t ? c.buttons[slot]! : defaultButtonForType(t),
        ),
      })),
    );
  };

  const setButtonSlot = (slot: 0 | 1, value: string | null) => {
    const types = [...layout.buttonTypes] as CardLayout['buttonTypes'];
    if (!value) {
      types.splice(slot, 1);
    } else {
      types[slot] = value as CardLayout['buttonTypes'][number];
    }
    changeLayout({ ...layout, buttonTypes: types.filter(Boolean).slice(0, 2) });
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= cards.length) return;
    const next = [...cards];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onCardsChange(next);
  };

  const detectVars = (text: string): number[] => {
    const out = new Set<number>();
    for (const m of text.matchAll(/\{\{(\d+)\}\}/g)) out.add(Number(m[1]));
    return [...out].sort((a, b) => a - b);
  };

  return (
    <Stack gap="xs">
      {/* Shared layout — Meta requires identical components on every card. */}
      <Card withBorder p="xs">
        <Text size="sm" fw={500} mb={4}>
          Card layout (applies to every card)
        </Text>
        <Group grow align="flex-end">
          <Select
            label="Media"
            size="xs"
            data={['IMAGE', 'VIDEO']}
            value={layout.headerFormat}
            onChange={(v) =>
              changeLayout({ ...layout, headerFormat: (v as 'IMAGE' | 'VIDEO') ?? 'IMAGE' })
            }
          />
          <Switch
            label="Cards have body text"
            size="xs"
            checked={layout.hasBody}
            onChange={(e) => changeLayout({ ...layout, hasBody: e.currentTarget.checked })}
          />
          <Select
            label="Button 1"
            size="xs"
            data={BUTTON_SLOT_OPTIONS}
            value={layout.buttonTypes[0] ?? ''}
            onChange={(v) => setButtonSlot(0, v)}
          />
          <Select
            label="Button 2"
            size="xs"
            data={BUTTON_SLOT_OPTIONS}
            value={layout.buttonTypes[1] ?? ''}
            onChange={(v) => setButtonSlot(1, v)}
            disabled={layout.buttonTypes.length === 0}
          />
        </Group>
      </Card>

      {cards.map((card, i) => (
        <Card key={i} withBorder p="xs">
          <Group justify="space-between" mb={4}>
            <Badge variant="light">
              Card {i + 1}/{cards.length}
            </Badge>
            <Group gap={2}>
              <ActionIcon variant="subtle" disabled={i === 0} onClick={() => move(i, -1)}>
                <IconArrowUp size={14} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                disabled={i === cards.length - 1}
                onClick={() => move(i, 1)}
              >
                <IconArrowDown size={14} />
              </ActionIcon>
              <ActionIcon
                color="red"
                variant="subtle"
                disabled={cards.length <= 2}
                onClick={() => removeCard(i)}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          </Group>
          <Stack gap={6}>
            <CardMediaUpload
              format={layout.headerFormat}
              uploaded={!!card.handleId}
              onUpload={(file) => handleCardUpload(i, file)}
            />
            {layout.hasBody && (
              <>
                <Textarea
                  label="Card body"
                  size="xs"
                  description={`${card.bodyText.length}/160 — positional variables ({{1}}) only`}
                  maxLength={160}
                  autosize
                  minRows={2}
                  value={card.bodyText}
                  onChange={(e) => {
                    const bodyText = e.currentTarget.value;
                    const vars = detectVars(bodyText);
                    updateCard(i, {
                      bodyText,
                      bodyExamples: vars.map((_, vi) => card.bodyExamples[vi] ?? ''),
                    });
                  }}
                />
                {detectVars(card.bodyText).map((n, vi) => (
                  <TextInput
                    key={n}
                    label={`Sample for {{${n}}}`}
                    size="xs"
                    value={card.bodyExamples[vi] ?? ''}
                    onChange={(e) => {
                      const next = [...card.bodyExamples];
                      next[vi] = e.currentTarget.value;
                      updateCard(i, { bodyExamples: next });
                    }}
                  />
                ))}
              </>
            )}
            {card.buttons.map((b, bi) => (
              <Group key={bi} grow>
                <TextInput
                  label={`${b.type === 'QUICK_REPLY' ? 'Quick reply' : b.type === 'URL' ? 'URL button' : 'Phone button'} label`}
                  size="xs"
                  maxLength={25}
                  value={b.text}
                  onChange={(e) => {
                    const buttons = [...card.buttons];
                    buttons[bi] = { ...b, text: e.currentTarget.value };
                    updateCard(i, { buttons });
                  }}
                />
                {b.type === 'URL' && (
                  <TextInput
                    label="URL"
                    size="xs"
                    value={b.url}
                    onChange={(e) => {
                      const buttons = [...card.buttons];
                      buttons[bi] = { ...b, url: e.currentTarget.value };
                      updateCard(i, { buttons });
                    }}
                  />
                )}
                {b.type === 'PHONE_NUMBER' && (
                  <TextInput
                    label="Phone (E.164)"
                    size="xs"
                    value={b.phone_number}
                    onChange={(e) => {
                      const buttons = [...card.buttons];
                      buttons[bi] = { ...b, phone_number: e.currentTarget.value };
                      updateCard(i, { buttons });
                    }}
                  />
                )}
              </Group>
            ))}
          </Stack>
        </Card>
      ))}

      <Button
        size="xs"
        variant="light"
        leftSection={<IconCirclePlus size={12} />}
        disabled={cards.length >= 10}
        onClick={() => onCardsChange([...cards, emptyCardDraft(layout)])}
      >
        Add card ({cards.length}/10)
      </Button>
    </Stack>
  );
}

/**
 * Per-card media upload. Owns its own `resetRef` (so re-picking the same file
 * re-fires onChange) and its own busy state (so only this card's button spins,
 * not every card sharing one mutation).
 */
function CardMediaUpload({
  format,
  uploaded,
  onUpload,
}: {
  format: 'IMAGE' | 'VIDEO';
  uploaded: boolean;
  onUpload: (file: File) => Promise<void>;
}) {
  const resetRef = useRef<() => void>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Group>
      <FileButton
        resetRef={resetRef}
        accept={format === 'IMAGE' ? 'image/*' : 'video/*'}
        onChange={(file) => {
          if (!file) return;
          setBusy(true);
          void onUpload(file).finally(() => {
            setBusy(false);
            resetRef.current?.();
          });
        }}
      >
        {(props) => (
          <Button
            {...props}
            size="xs"
            variant="light"
            leftSection={<IconUpload size={12} />}
            loading={busy}
          >
            Upload {format.toLowerCase()}
          </Button>
        )}
      </FileButton>
      {uploaded && (
        <Badge color="green" variant="light">
          Uploaded ✓
        </Badge>
      )}
    </Group>
  );
}
